import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { Row } from 'primereact/row'
import React, { useContext, useEffect, useState, useRef } from 'react'
import { getZoneRegions } from '../../api/axiosRegions'
import { getRegionDistricts } from '../../api/axiosDistricts'
import DataContext from '../../context/DataContext'
import { Card } from 'primereact/card'
import { Divider } from 'primereact/divider'
import { getAllZones } from '../../api/axiosZones'
import { getAllMonitors } from '../../api/axiosMonitors'

import { capitalizeFirstLetter, formatTIN } from '../../utilities/UtilityFunctions'
import { Toast } from 'primereact/toast'
import { incidentsLocationRpt } from '../../api/reports/incidentsLocationrpt'
import { useTranslation } from 'react-i18next'
const IncidentPerAttribute = ({ monitorReport, attributeType, disaggregation }) => {
    const { t } = useTranslation();
    const {
        fetcherror, setFetcherror,
        broadCategories, setBroadCategories,
        categories, setCategories,
        subCategories, setSubCategories,
        rights, setRights,
        religions, setReligions,
        tribes, setTribes,
        educationLevels, setEducationLevels,
        employmentStatuses, setEmploymentStatuses,
        employmentSectors, setEmploymentSectors,
        maritalStatuses, setMaritalStatuses,
        marriageTypes, setMarriageTypes,
        vulnerabilities, setVulnerabilities,
        disabilities, setDisabilities,
        roles, setRoles, 
        auntheticatedUser
    } = useContext(DataContext);

    const toast = useRef(null);
    
    const isLHRCUser = auntheticatedUser?.role === "Admin" || auntheticatedUser?.role === "Operator" || auntheticatedUser?.role === "LHRCStaff";
    
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState("");
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [districts, setDistricts] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [monitors, setMonitors] = useState([]);
    const [selectedMonitor, setSelectedMonitor] = useState("");
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportingPDF, setExportingPDF] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportingCSV, setExportingCSV] = useState(false);

    useEffect(() => {
        if (monitorReport) { 
            getAllMonitors(setMonitors, setFetcherror); 
        }
        getAllZones(setZones, setFetcherror);
    }, [])

    const newAttributeType = attributeType?.toLowerCase();
    const newDisaggregation = disaggregation ? disaggregation : "unknown"

    const genetateTotals = (disaggregation) => {
        if (disaggregation === "validity") {
            const totalValid = reportData?.reduce((total, data) => total + data.valid, 0);
            const totalInvalid = reportData?.reduce((total, data) => total + data.invalid, 0);
            const totalSubtotal = reportData?.reduce((total, data) => total + data.total, 0);

            const totalsRow = {
                attributetype: 'TOTAL',
                valid: totalValid,
                invalid: totalInvalid,
                total: totalSubtotal
            };
            return [...reportData, totalsRow]
        } else if (disaggregation === "sex") {
            const totalMale = reportData?.reduce((total, data) => total + data.male, 0);
            const totalFemale = reportData?.reduce((total, data) => total + data.female, 0);
            const totalSubtotal = reportData?.reduce((total, data) => total + data.total, 0);

            const totalsRow = {
                attributetype: 'TOTAL',
                male: totalMale,
                female: totalFemale,
                total: totalSubtotal
            };
            return [...reportData, totalsRow]
        } else {
            return reportData
        }
    }

    const formatStartDate = (d) => {
        if (!d) return "";
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
    }

    const formatEndDate = (d) => {
        if (!d) return "";
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 1) - 1).toISOString();
    }

    // Handle export download
    const handleExport = async (exportType) => {
        // Set loading state based on export type
        if (exportType === 'pdf') setExportingPDF(true);
        else if (exportType === 'xlsx' || exportType === 'excel') setExportingExcel(true);
        else if (exportType === 'csv') setExportingCSV(true);

        await incidentsLocationRpt(
            selectedZone?.name,
            selectedRegion?.name,
            selectedDistrict?.name,
            formatStartDate(startDate),
            formatEndDate(endDate),
            newAttributeType,
            setReportData,
            setFetcherror,
            monitorReport,
            isLHRCUser ? selectedMonitor?.id : auntheticatedUser?.id,
            newDisaggregation,
            true, // exportReport
            exportType,
            (result, error) => {
                // Reset loading states
                setExportingPDF(false);
                setExportingExcel(false);
                setExportingCSV(false);

                if (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Export Failed',
                        detail: error,
                        life: 5000
                    });
                } else if (result?.success) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Export Successful',
                        detail: `${result.filename} downloaded successfully`,
                        life: 3000
                    });
                }
            }
        );
    };

    // Handle generate report
    const handleGenerateReport = async () => {
        setLoading(true);
        
        await incidentsLocationRpt(
            selectedZone?.name,
            selectedRegion?.name,
            selectedDistrict?.name,
            formatStartDate(startDate),
            formatEndDate(endDate),
            newAttributeType,
            setReportData,
            setFetcherror,
            monitorReport,
            isLHRCUser ? selectedMonitor?.id : auntheticatedUser?.id,
            newDisaggregation,
            false, // exportReport
            null,
            (result, error) => {
                setLoading(false);
                
                if (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error,
                        life: 5000
                    });
                } else {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Report generated successfully',
                        life: 3000
                    });
                }
            }
        );
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={capitalizeFirstLetter(attributeType)} rowSpan={2} style={{ width: '55%' }} />
                <Column header={<div style={{ textAlign: "right" }}>Status</div>} colSpan={2} style={{ width: '30%', textAlign: "right" }} />
                <Column header="SubTotal" rowSpan={2} style={{ width: '15%' }} />
            </Row>
            <Row>
                <Column header="Valid" field="valid" style={{ width: '15%', textAlign: "center" }} />
                <Column header="Invalid" field="invalid" style={{ width: '15%', textAlign: "center" }} />
            </Row>
        </ColumnGroup>
    );

    const headerGroupSex = (
        <ColumnGroup>
            <Row>
                <Column header={capitalizeFirstLetter(attributeType)} rowSpan={2} style={{ width: '55%' }} />
                <Column header={<div style={{ textAlign: "right" }}>Sex Victim</div>} colSpan={2} style={{ width: '30%', textAlign: "right" }} />
                <Column header="SubTotal" rowSpan={2} style={{ width: '15%' }} />
            </Row>
            <Row>
                <Column header="Male" field="male" style={{ width: '15%', textAlign: "center" }} />
                <Column header="Female" field="female" style={{ width: '15%', textAlign: "center" }} />
            </Row>
        </ColumnGroup>
    );

    const headerGroupMonitors = (
        <ColumnGroup>
            <Row>
                <Column header="Name" rowSpan={2} style={{ width: '25%' }} />
                <Column header="Region" rowSpan={2} style={{ width: '15%' }} />
                <Column header="District" rowSpan={2} style={{ width: '15%' }} />
                <Column header="Phone" rowSpan={2} style={{ width: '15%' }} />
                <Column header="TIN" rowSpan={2} style={{ width: '15%' }} />
                <Column header={<div style={{ textAlign: "right" }}>Status</div>} colSpan={2} style={{ width: '20%', textAlign: "right" }} />
                <Column header="SubTotal" rowSpan={2} style={{ width: '10%' }} />
            </Row>
            <Row>
                <Column header="Valid" field="valid" style={{ width: '10%', textAlign: "center" }} />
                <Column header="Invalid" field="invalid" style={{ width: '10%', textAlign: "center" }} />
            </Row>
        </ColumnGroup>
    );

    return (
        <div>
            <Toast ref={toast} />
            
            <Card>
                <div className='flex justify-content-end gap-2'>
                    <Button
                        icon="pi pi-file-pdf"
                        size="large"
                        tooltip="Export as PDF"
                        tooltipOptions={{ position: 'bottom' }}
                        severity="danger"
                        text
                        className="py-0 px-2"
                        loading={exportingPDF}
                        disabled={!reportData || reportData.length === 0 || exportingExcel || exportingCSV}
                        onClick={() => handleExport('pdf')}
                    />
                    <Button
                        icon="pi pi-file-excel"
                        size="large"
                        tooltip="Export as Excel"
                        tooltipOptions={{ position: 'bottom' }}
                        severity="success"
                        text
                        className="py-0 px-2"
                        loading={exportingExcel}
                        disabled={!reportData || reportData.length === 0 || exportingPDF || exportingCSV}
                        onClick={() => handleExport('xlsx')}
                    />
                    <Button
                        icon="pi pi-file"
                        size="large"
                        tooltip="Export as CSV"
                        tooltipOptions={{ position: 'bottom' }}
                        severity="info"
                        text
                        className="py-0 px-2"
                        loading={exportingCSV}
                        disabled={!reportData || reportData.length === 0 || exportingPDF || exportingExcel}
                        onClick={() => handleExport('csv')}
                    />
                </div>

                <div className='my-4'>
                    <Divider align="start" type='solid' style={{ color: "black", width: '100%' }}>
                        <span className="p-tag text-base mx-2 bg-white text-primary">
                            <i className="pi pi-chart-bar" style={{ fontSize: '0.7rem', marginRight: '0.5rem' }}></i>
                            {disaggregation === "validity" ? "PROCESSED " : disaggregation === "sex" ? "VALID " : ""}{monitorReport ? "MONITOR" : ""} INCIDENTS {attributeType === "user_id" ? " " : `PER ${attributeType} `}REPORT
                        </span>
                    </Divider>
                </div>

                <div>
                    <div className="flex flex-row justify-content-between mb-4 w-full">
                        <div className="flex justify-content-center w-10 gap-2">
                            {(attributeType === "ZONE" || attributeType === "REGION" || attributeType === "DISTRICT") &&
                                <div className="flex-auto">
                                    <Dropdown
                                        value={selectedZone}
                                        onChange={(e) => {
                                            setSelectedZone(e.value);
                                            getZoneRegions(setRegions, setFetcherror, e.value?.id);
                                        }}
                                        options={zones}
                                        optionLabel="name"
                                        placeholder="Select Zone"
                                        showClear
                                        className="w-full"
                                    />
                                </div>
                            }

                            {(attributeType === "REGION" || attributeType === "DISTRICT") &&
                                <div className="flex-auto">
                                    <Dropdown
                                        value={selectedRegion}
                                        onChange={(e) => {
                                            setSelectedRegion(e.value);
                                            getRegionDistricts(setDistricts, setFetcherror, e.value?.id);
                                        }}
                                        options={regions}
                                        optionLabel="name"
                                        placeholder="Select Region"
                                        showClear
                                        className="w-full"
                                    />
                                </div>
                            }

                            {(attributeType === "DISTRICT") &&
                                <div className="flex-auto">
                                    <Dropdown
                                        value={selectedDistrict}
                                        onChange={(e) => {
                                            setSelectedDistrict(e.value);
                                        }}
                                        options={districts}
                                        optionLabel="name"
                                        placeholder="Select District"
                                        showClear
                                        className="w-full"
                                    />
                                </div>
                            }

                            {monitorReport && isLHRCUser &&
                                <div className="flex-auto">
                                    <Dropdown
                                        value={selectedMonitor}
                                        onChange={(e) => {
                                            setSelectedMonitor(e.value);
                                        }}
                                        options={monitors}
                                        optionLabel="namedistrict"
                                        placeholder={t("Select Monitor")}
                                        filter
                                        filterBy="namedistrict"
                                        filterPlaceholder={t("Search Monitor")}
                                        showClear
                                        className="w-full"
                                    />
                                </div>
                            }

                            <div className="flex-auto">
                                <Calendar
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.value);
                                    }}
                                    className='w-full'
                                    placeholder='Select Start Date'
                                    dateFormat='dd/mm/yy'
                                />
                            </div>

                            <div className="flex-auto">
                                <Calendar
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.value);
                                    }}
                                    className='w-full'
                                    placeholder='Select End Date'
                                    dateFormat='dd/mm/yy'
                                />
                            </div>
                        </div>

                        <div className='flex flex-column justify-content-end'>
                            <Button
                                icon="pi pi-search"
                                label="Generate"
                                outlined
                                iconPos="left"
                                className="py-2 px-3"
                                loading={loading}
                                onClick={handleGenerateReport}
                            />
                        </div>
                    </div>

                    {disaggregation === "validity" && <>
                        {(attributeType !== "user_id" && monitorReport) &&
                            <DataTable value={genetateTotals("validity")} showGridlines headerColumnGroup={headerGroup} emptyMessage="No Items" loading={loading}>
                                <Column field={attributeType} body={(rowData) => rowData?.attributetype} sortable style={{ width: '55%' }}></Column>
                                <Column field="valid" body={(rowData) => rowData?.valid} style={{ width: '15%' }}></Column>
                                <Column field="invalid" body={(rowData) => rowData?.invalid} sortable style={{ width: '15%' }}></Column>
                                <Column field="subtotal" body={(rowData) => rowData.total} sortable style={{ width: '15%' }}></Column>
                            </DataTable>
                        }

                        {(attributeType === "user_id" && monitorReport) &&
                            <DataTable value={reportData} showGridlines headerColumnGroup={headerGroupMonitors} emptyMessage="No Items" loading={loading}>
                                <Column field='name' body={(rowData) => rowData?.firstname + " " + rowData?.lastname} sortable style={{ width: '25%' }}></Column>
                                <Column field='region' body={(rowData) => rowData?.region} sortable style={{ width: '15%' }}></Column>
                                <Column field='district' body={(rowData) => rowData?.district} sortable style={{ width: '15%' }}></Column>
                                <Column field='phone' body={(rowData) => rowData?.phone} sortable style={{ width: '15%' }}></Column>
                                <Column field='tin' body={(rowData) => formatTIN(rowData?.tin)} sortable style={{ width: '25%' }}></Column>
                                <Column field="valid" body={(rowData) => rowData?.valid} style={{ width: '10%' }}></Column>
                                <Column field="invalid" body={(rowData) => rowData?.invalid} sortable style={{ width: '10%' }}></Column>
                                <Column field="subtotal" body={(rowData) => rowData.total} sortable style={{ width: '10%' }}></Column>
                            </DataTable>
                        }

                        {!monitorReport &&
                            <DataTable value={genetateTotals("validity")} showGridlines headerColumnGroup={headerGroup} emptyMessage="No Items" loading={loading}>
                                <Column field={attributeType} body={(rowData) => rowData?.attributetype} sortable style={{ width: '55%' }}></Column>
                                <Column field="valid" body={(rowData) => rowData?.valid} style={{ width: '15%' }}></Column>
                                <Column field="invalid" body={(rowData) => rowData?.invalid} sortable style={{ width: '15%' }}></Column>
                                <Column field="subtotal" body={(rowData) => rowData.total} sortable style={{ width: '15%' }}></Column>
                            </DataTable>
                        }
                    </>}

                    {disaggregation === "sex" && <>
                        <DataTable value={genetateTotals("sex")} showGridlines headerColumnGroup={headerGroupSex} emptyMessage="No Items" loading={loading}>
                            <Column field={attributeType} body={(rowData) => rowData?.attributetype} sortable style={{ width: '55%' }}></Column>
                            <Column field="male" body={(rowData) => rowData?.male} style={{ width: '15%' }}></Column>
                            <Column field="female" body={(rowData) => rowData?.female} sortable style={{ width: '15%' }}></Column>
                            <Column field="subtotal" body={(rowData) => rowData.total} sortable style={{ width: '15%' }}></Column>
                        </DataTable>
                    </>}
                </div>
            </Card>
        </div>
    )
}

export default IncidentPerAttribute